"use client";

import type { SupWeb3DepositStep, SupWeb3WithdrawSingleVault, SupWithdrawableToken, SupWeb3VaultBalance, Networkish } from "@/types";
import React, { useEffect, useState } from "react";

import { useStoredData } from "@/store";
import { BigNumber, FixedNumber } from "@ethersproject/bignumber";
import useWithdrawCalculate from "@/hooks/tx-flow/useWithdrawCalculate";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";

import RouteStateProvider from "@/components/Web3/Common/Route/State";
import Web3CommonStateDebug from "@/components/Web3/Debug/State";
import Web3WithdrawTargetAmount from "@/components/Web3/Withdraw/Target/Amount";
import Web3WithdrawTargetToken from "@/components/Web3/Withdraw/Target/Token";
import Web3CommonSelectedToken from "@/components/Web3/Common/SelectedToken";
import Web3CommonVaultAccordion from "@/components/Web3/Common/Vault/Accordion";
import Web3CommonHeader from "@/components/Web3/Common/Header";
import Web3StepConfirmation from "@/components/Web3/Withdraw/Confirmation";
import Web3CommonFinished from "@/components/Web3/Common/Finished";

import { getVaultDecimals } from "@/utils";
import { twMerge } from "tailwind-merge";
import { trackBagOpen } from "@/utils/analytics";

export default function Web3Withdraw({ superPositions, sourceChainId, className }: { superPositions: SupWeb3VaultBalance[]; sourceChainId: number; className?: string }) {
  const [step, setStep] = useState<SupWeb3DepositStep>("Default" as SupWeb3DepositStep);
  const [vaultList, setVaultList] = useState<SupWeb3WithdrawSingleVault[]>([] as SupWeb3WithdrawSingleVault[]);
  const [totalGasCost, setTotalGasCost] = useState<number | undefined>(undefined);

  const [selectedBalance, setSelectedBalance] = useState<SupWithdrawableToken | undefined>(undefined);
  const [transferTx, setTransferTx] = useState<string | undefined>(undefined);
  const [pauseRequests, setPauseRequests] = useState<boolean>(false);
  const [simulate, setSimulate] = useState<boolean>(false);

  const userAddress = useWeb3UserAddress();

  useEffect(() => {
    if (selectedBalance) {
      setStep("Calculate");
    }
  }, [selectedBalance]);

  const storedData = useStoredData();

  useEffect(() => {
    if (storedData.txCartValue && storedData.txCartValue.cart === "withdraw" && !selectedBalance && storedData.txCartValue.selectedBalance) {
      setSelectedBalance(storedData.txCartValue.selectedBalance as SupWithdrawableToken);
    }
  }, [selectedBalance, storedData.txCartValue]);

  useEffect(() => {
    if (superPositions.length > 0 && userAddress) {
      trackBagOpen(
        superPositions.map(position => position.vault),
        "withdraw",
        userAddress
      );
    }
  }, [superPositions, userAddress]);

  useEffect(() => {
    if (!superPositions) {
      setVaultList([] as SupWeb3WithdrawSingleVault[]);
    } else {
      const vaultListPrepared = superPositions.map(singlePosition => {
        const decimals = getVaultDecimals(singlePosition.vault);
        const balance =
          decimals > 18 ? FixedNumber.from(`${singlePosition.superposition_balance}`) : FixedNumber.fromValue(BigNumber.from(`${singlePosition.superposition_balance}`), decimals);

        const balanceInFiat = FixedNumber.from(parseFloat(`${singlePosition.superposition_usd_value}`).toFixed(8));

        const amount =
          storedData.txCartValue && storedData.txCartValue.cart === "withdraw" && storedData.txCartValue.totalAmount
            ? FixedNumber.from(storedData.txCartValue.totalAmount)
            : balance;
        const price = balanceInFiat.divUnsafe(balance);

        const amountInFiat = storedData.txCartValue && storedData.txCartValue.cart === "withdraw" && storedData.txCartValue.totalAmount ? amount.mulUnsafe(price) : balanceInFiat;
        return {
          ...singlePosition.vault,
          chainId: singlePosition.chain_id,
          balance: balance,
          balanceInFiat: balanceInFiat,
          amount,
          amountInFiat,
          superposition_id: singlePosition.superposition_id,
        } as SupWeb3WithdrawSingleVault;
      });
      setVaultList(vaultListPrepared);
    }
  }, [superPositions, selectedBalance, storedData.txCartValue]);

  const withdrawalMultiCalculate = useWithdrawCalculate(selectedBalance, vaultList, pauseRequests);

  return (
    <Web3CommonStateDebug type="withdraw">
      <RouteStateProvider type="withdraw" txCalculate={withdrawalMultiCalculate} vaultList={vaultList}>
        <div className={twMerge("flex h-full max-h-screen w-full flex-grow flex-col items-stretch overflow-x-hidden p-6 pt-0 lg:justify-between", className)}>
          <Web3CommonHeader step={step} setStep={setStep} type="withdraw" />
          {["Default", "Calculate"].includes(step) && (
            <>
              {selectedBalance && (
                <Web3CommonSelectedToken
                  onChange={() => {
                    setStep("Balance");
                    setSelectedBalance(undefined);
                  }}
                  selectedBalance={selectedBalance}
                  type="withdraw"
                />
              )}
              {selectedBalance && <Web3WithdrawTargetAmount setVaultList={setVaultList} vaultList={vaultList} selectedBalance={selectedBalance} />}
              {
                <Web3CommonVaultAccordion
                  setPauseRequests={setPauseRequests}
                  // @ts-ignore
                  setVaultList={setVaultList}
                  // @ts-ignore
                  selectedBalance={selectedBalance}
                  type="withdraw"
                  step={step}
                  vaultList={vaultList}
                  setStep={setStep}
                  pauseRequests={pauseRequests}
                  calculatedRoutes={withdrawalMultiCalculate}
                />
              }
              {vaultList.length > 0 && (
                <Web3StepConfirmation
                  simulate={simulate}
                  setSimulate={setSimulate}
                  targetChainId={sourceChainId}
                  vaultList={vaultList}
                  step={step}
                  setStep={setStep}
                  setTransferTx={setTransferTx}
                  selectedBalance={selectedBalance}
                  totalGasCost={totalGasCost}
                  setTotalGasCost={setTotalGasCost}
                  setPauseRequests={setPauseRequests}
                  calculatedRoutes={withdrawalMultiCalculate}
                />
              )}
            </>
          )}

          {step === "Balance" && <Web3WithdrawTargetToken vaultList={vaultList} setSelectedBalance={setSelectedBalance} />}

          {step === "Finished" && (
            <Web3CommonFinished vaultList={vaultList} selectedBalance={selectedBalance} chainId={sourceChainId as Networkish} type="withdraw" transferTx={transferTx} />
          )}
        </div>
      </RouteStateProvider>
    </Web3CommonStateDebug>
  );
}
